import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { Paginator } from 'primeng/paginator';
import { Subscription, Observable } from 'rxjs';
import { ConfirmationService } from 'primeng/api';

@Component({
  selector: 'app-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss'],

  providers: [ConfirmationService],
})
export class TableComponent implements OnInit, OnDestroy {
  /**
   * @description
   * Data display table
   */

  /**
   * * General
   * @numberOfCols Calc number of cals and set that as colspan for generic message when table is empty
   */
  subscriptions: Subscription[] = [];
  numberOfCols: number;
  /**
   * * Outputs
   * @handlePageChange Event when page in paginator is changed
   * @handleStatusChange Event when post/page status is changed
   * (Status indicates whether the page is visible on the site or not)
   * @handleDelete Event when row in table is deleted
   */
  @Output() handlePageChange = new EventEmitter<any>();
  @Output() handleStatusChange = new EventEmitter<any>();
  @Output() handleDelete = new EventEmitter<any>();
  @Output() handlePreview = new EventEmitter<any>();
  @Output() handleSelect = new EventEmitter<any>();
  @Output() handleModal = new EventEmitter<any>();
  @Output() handleSort = new EventEmitter<any>();
  @Output() handleCopy = new EventEmitter<any>();

  /**
   * * Inputs
   * @loading Set table spinner
   * @tableHeader Array of table headers ['name', 'surname']
   * @tableData Actual table data
   * @editPath Determines how url path for edit shoud look: /#/edit/23
   * @options Additional table options
   */
  @Input() rowsPerPage;
  @Input() actionLabel = 'Actions';
  @Input() resetSelected: Observable<void>;
  @Input() paginator: any | any;
  @Input() frontendPaginator = false;
  @Input() loading = false;
  @Input() enableRowSelect = false;
  @Input() tableHeader: any[];
  @Input() tableData: any;
  @Input() editPath: 'edit' | 'post_template' | 'page-builder' = 'edit';
  @Input() options: {
    showPreview?: boolean;
    showEdit?: boolean;
    showDelete?: boolean;
    showStatus?: boolean;
    showSwitch?: boolean;
    showClone?: boolean;
    switchText?: string;
    showModal?: boolean;
    showCopy?: boolean;
  } = {
    showPreview: false,
    showEdit: false,
    showDelete: false,
    showStatus: false,
    showSwitch: false,
    showClone: false,
    switchText: 'Rešen',
    showModal: false,
    showCopy: false,
  };
  selectedRows;
  @ViewChild('paginatorElement') paginatorElement: Paginator;

  constructor(
    private cdr: ChangeDetectorRef,
    private confirmationService: ConfirmationService
  ) {}

  /* ====================================
   *              LIFECYCLES
   ======================================= */

  ngOnInit(): void {
    /**
     * Calc number of cols
     */
    this.numberOfCols =
      this.tableHeader.length +
      ((this.options.showDelete && this.options.showEdit) ||
      this.options.showPreview
        ? 1
        : 0) +
      (this.options.showSwitch ? 1 : 0);

    if (this.resetSelected) {
      this.subscriptions.push(
        this.resetSelected.subscribe((res) => {
          this.selectedRows = [];
        })
      );
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    this.resetSort();
  }

  /* ====================================
   *             MAIN FEATURES
   ======================================= */

  private updateCurrentPage(currentPage: number): void {
    this.paginatorElement.changePage(currentPage - 1);
  }

  onPageChange(event: any) {
    this.handlePageChange.emit(event);
  }

  onChangeStatus(event: any, id: number) {
    this.handleStatusChange.emit({ event, id });
  }

  deleteRow(event: Event, id: number) {
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: 'Da li ste sigurni da želite da obrišete ovu stavku?',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Da',
      rejectLabel: 'Ne',
      accept: () => {
        this.handleDelete.emit({ event, id });
        if (this.paginator) this.paginatorElement.changePageToFirst(event);
      },
    });
  }

  onRowSelect() {
    this.handleSelect.emit(this.selectedRows);
  }
  onSelectAll() {}

  previewRow(event: Event, data: any) {
    this.handlePreview.emit({ event, data });
  }

  openModal(event, data) {
    this.handleModal.emit({ event, data });
  }

  copyToClipboard(event, data) {
    this.handleCopy.emit({ event, data });
  }

  onSort(column) {
    if (column.sort) {
      const query = `sort=${column.sort.sortParam}&order=${column.sort.sortDirection}`;

      /**
       * Toggle asc and desc icons change
       */

      if (column.sort.sortDirection === 'asc') {
        column.sort.sortDirection = 'desc';
      } else {
        column.sort.sortDirection = 'asc';
      }

      /**
       * Add active-sort-class to clicked icon
       */
      column.sort.activeSort = true;

      this.tableHeader.forEach((header) => {
        if (header.sort) {
          header.sort.activeSort = false;

          if (header.field === column.field) {
            header.sort.activeSort = true;
          }
        }
      });

      /**
       * Emit query for sorting
       */
      this.handleSort.emit({ event, query });
    }
  }

  resetSort() {
    this.tableHeader.forEach((header) => {
      header.activeSort = false;
      header.sortDirection = 'asc';
    });
  }

  proxyClick(event: Event, rowData, callback: Function) {
    event.stopPropagation();
    callback(event, rowData);
  }
}
